/**
 * Environment Configuration
 * Defines environment-specific settings for development and production
 */

import type { Environment, FeatureFlags } from '@schoolidb/types';

/**
 * Environment-specific configuration
 */
export interface EnvironmentConfig {
  /** Environment name */
  name: Environment;
  /** Base URL for API requests */
  apiBaseUrl: string;
  /** Google Maps API key */
  googleMapsApiKey: string;
  /** Google OAuth client ID */
  googleClientId: string;
  /** Sentry DSN for error tracking */
  sentryDsn: string;
  /** Analytics tracking ID */
  analyticsId: string;
  /** Feature flags for this environment */
  features: FeatureFlags;
  /** Enable debug mode */
  debug: boolean;
  /** AWS region for services */
  awsRegion: string;
}

/**
 * Get the current environment name
 * Vite replaces import.meta.env.* at build time, so we must use direct property access
 */
export const getEnvironment = (): Environment => {
  // Vite replaces these at build time - must use direct property access, not dynamic
  const env = import.meta.env?.VITE_ENV || import.meta.env?.MODE || 'development';
  
  if (env === 'production' || env === 'prod') {
    return 'production';
  }
  if (env === 'staging') {
    return 'staging';
  }
  return 'development';
};

/**
 * Get the configuration for the current environment
 * Uses direct import.meta.env access for Vite static replacement at build time
 */
export const getEnvironmentConfig = (): EnvironmentConfig => {
  const env = getEnvironment();
  
  // Base feature flags
  const baseFeatures: FeatureFlags = {
    aiSearch: true,
    schoolComparison: true,
    communityFeed: true,
    darkMode: true,
    mapView: true,
    voiceSearch: true,
    offlineMode: true,
    pushNotifications: true,
    biometricAuth: true,
    debugPanel: env !== 'production',
    mockApi: false,
  };

  // Vite replaces import.meta.env.VITE_* at build time
  // Must use direct property access, not dynamic key access
  const config: EnvironmentConfig = {
    name: env,
    apiBaseUrl: import.meta.env?.VITE_API_BASE_URL || (env === 'production' ? 'https://api.schoolidb.com' : 'https://dev-api.schoolidb.com'),
    googleMapsApiKey: import.meta.env?.VITE_GOOGLE_MAPS_KEY || '',
    googleClientId: import.meta.env?.VITE_GOOGLE_CLIENT_ID || '',
    sentryDsn: import.meta.env?.VITE_SENTRY_DSN || '',
    analyticsId: import.meta.env?.VITE_ANALYTICS_ID || '',
    awsRegion: 'ap-south-1',
    features: baseFeatures,
    debug: env !== 'production',
  };

  return config;
};

/**
 * Check if running in development mode
 */
export const isDevelopment = (): boolean => getEnvironment() === 'development';

/**
 * Check if running in production mode
 */
export const isProduction = (): boolean => getEnvironment() === 'production';

/**
 * Check if running in staging mode
 */
export const isStaging = (): boolean => getEnvironment() === 'staging';

/**
 * Check if debug mode is enabled
 */
export const isDebugEnabled = (): boolean => getEnvironmentConfig().debug;

// Legacy exports for backwards compatibility (now computed dynamically)
export const developmentConfig = getEnvironmentConfig();
export const productionConfig = getEnvironmentConfig();
export const stagingConfig = getEnvironmentConfig();
export const environments: Record<Environment, EnvironmentConfig> = {
  development: getEnvironmentConfig(),
  staging: getEnvironmentConfig(),
  production: getEnvironmentConfig(),
};
